import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import type { ApiResponse } from '../types';

/**
 * API 客户端配置
 * 基于 Axios 封装的 HTTP 请求客户端，支持多端独立 token 管理
 */

/** 请求超时时间（毫秒） */
const REQUEST_TIMEOUT = 300000;

/** 客户端 token 配置（可由各端初始化时覆盖） */
const tokenConfig = {
  tokenKey: 'admin_token',
  refreshTokenKey: 'admin_refresh_token',
  refreshEndpoint: '/api/admin/auth/refresh-token',
};

/**
 * 配置 token 存储键名和刷新端点
 * 各端（admin-portal / user-portal）在入口处调用此函数进行初始化
 */
export function configureTokenKeys(options: {
  tokenKey: string;
  refreshTokenKey: string;
  refreshEndpoint: string;
}) {
  tokenConfig.tokenKey = options.tokenKey;
  tokenConfig.refreshTokenKey = options.refreshTokenKey;
  tokenConfig.refreshEndpoint = options.refreshEndpoint;
}

/**
 * 读取运行时后端地址
 * 优先从 window.__CONFIG__.API_BASE_URL 读取（生产环境由 config.js 注入），
 * 未配置时回退到相对路径（开发环境由 Vite proxy 转发）
 */
function getApiBaseUrl(): string {
  const runtimeBase = (window as any).__CONFIG__?.API_BASE_URL;
  if (runtimeBase) return runtimeBase + '/api';
  return '/api';
}

/**
 * 创建 axios 实例
 * 配置基础URL、超时时间和默认请求头
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * 获取后端服务器基础URL（不含 /api 前缀）
 * 用于拼接图片等静态资源的相对路径
 * 开发环境返回 http://localhost:5000，生产环境返回空字符串
 */
export function getBackendBaseUrl(): string {
  return (window as any).__CONFIG__?.API_BASE_URL || '';
}

/**
 * 请求拦截器 - 自动添加认证令牌
 * 从 localStorage 获取 token 并添加到请求头
 */
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(tokenConfig.tokenKey);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * 响应拦截器 - 处理令牌刷新和全局错误
 * 401 错误时自动尝试刷新令牌，失败则跳转登录页
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse<ApiResponse<any>>) => {
    const result = response.data;
    // 业务层面失败（isSuccess=false）时，提取错误信息并抛出
    if (result && result.isSuccess === false) {
      const businessError = new Error(result.error || result.message || '操作失败');
      (businessError as any).code = result.code;
      (businessError as any).isSuccess = false;
      return Promise.reject(businessError);
    }
    // 成功时返回 data 字段
    return result.data;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // 处理 401 未授权错误，尝试刷新令牌
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem(tokenConfig.refreshTokenKey);
        if (!refreshToken) {
          throw new Error('No refresh token');
        }
        
        // 调用刷新令牌接口
        const response = await axios.post(tokenConfig.refreshEndpoint, {
          refreshToken,
        });
        
        const { accessToken, refreshToken: newRefreshToken } = response.data.data;
        localStorage.setItem(tokenConfig.tokenKey, accessToken);
        localStorage.setItem(tokenConfig.refreshTokenKey, newRefreshToken);
        
        // 使用新令牌重试原请求
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // 刷新失败，清除令牌并跳转登录页
        localStorage.removeItem(tokenConfig.tokenKey);
        localStorage.removeItem(tokenConfig.refreshTokenKey);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    // 提取后端返回的业务错误信息，包装成更友好的 Error 对象
    const errorData = error.response?.data;
    const businessMessage = errorData?.error || errorData?.message || error.message || '请求失败';
    const businessError = new Error(businessMessage);
    (businessError as any).code = errorData?.code || error.response?.status;
    (businessError as any).isSuccess = errorData?.isSuccess;
    return Promise.reject(businessError);
  }
);

export default apiClient;

/**
 * 通用 HTTP 请求方法封装
 * 提供类型安全的 GET、POST、PUT、DELETE、PATCH 方法
 */
export const http = {
  /**
   * GET 请求
   * @template T 响应数据类型
   * @param url 请求地址
   * @param config 请求配置
   * @returns Promise<T>
   */
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.get(url, config) as Promise<T>,
  
  /**
   * POST 请求
   * @template T 响应数据类型
   * @param url 请求地址
   * @param data 请求体数据
   * @param config 请求配置
   * @returns Promise<T>
   */
  post: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.post(url, data, config) as Promise<T>,
  
  /**
   * PUT 请求
   * @template T 响应数据类型
   * @param url 请求地址
   * @param data 请求体数据
   * @param config 请求配置
   * @returns Promise<T>
   */
  put: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.put(url, data, config) as Promise<T>,
  
  /**
   * DELETE 请求
   * @template T 响应数据类型
   * @param url 请求地址
   * @param config 请求配置
   * @returns Promise<T>
   */
  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.delete(url, config) as Promise<T>,
  
  /**
   * PATCH 请求
   * @template T 响应数据类型
   * @param url 请求地址
   * @param data 请求体数据
   * @param config 请求配置
   * @returns Promise<T>
   */
  patch: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.patch(url, data, config) as Promise<T>,
};
